import * as React from 'react';
import styled from 'styled-components';
import { DateInput, DatePickerModifiers } from '@blueprintjs/datetime';
import FieldDecorator from './FieldDecorator';
import { getLocaleDateStringInIsoFormat, hasValue, getLocaleDateFromFormatString } from '../../lib/utils';
import './fieldStyles.less';
import { getCultureString } from '../../core/internationalisation';
import { baseInputCss } from './StyledElements';

const DateField = (props: Props) => {
	const { min, max, required } = props;
	let dateValue;
	if (props.value) {
		dateValue = new Date(props.value);
	}
	const minDate = new Date(1900, 1, 1);
	const maxDate = new Date(2100, 12, 31);
	const [errorMessageState, setErrorMessageState] = React.useState('');

	const handleDateChange = (date: Date | null) => {
		const updatedErrorMessage = getErrorMessage(date);
		setErrorMessageState(updatedErrorMessage);
		props.onValueChange(props.name, getLocaleDateStringInIsoFormat(date), null);
	};

	const getErrorMessage = (date: Date | null) => {
		if (props.errorMessage) {
			return props.errorMessage;
		}
		if (!hasValue(date) && required) {
			return 'Please fill out this field.';
		}
		if (hasValue(date)) {
			date.setHours(0, 0, 0, 0);
			if (hasValue(min) && date < min) return `${props.label} date must not be before ${min?.toLocaleDateString()}.`;
			if (hasValue(max) && date > max) return `${props.label} date must not be after ${max?.toLocaleDateString()}.`;
		}
		return null;
	};

	const blueprintModifiers: DatePickerModifiers = {
		blueprint: () => true
	};

	return (
		<FieldDecorator
			id={props.fieldId}
			label={props.label}
			htmlFor={props.name}
			errorMessage={errorMessageState}
			displayOptionalLabel={props.displayOptionalLabel}
		>
			<DateInputWrapper width={props.width}>
				<DateInput
					value={dateValue}
					defaultValue={props.defaultValue}
					formatDate={(date) => date?.toLocaleDateString(getCultureString())}
					parseDate={(str) => getLocaleDateFromFormatString(str, 'dd/MM/yyyy')}
					minDate={minDate}
					maxDate={maxDate}
					onChange={handleDateChange}
					disabled={props.disabled}
					inputProps={{ required }}
					modifiers={blueprintModifiers}
					fill
				/>
			</DateInputWrapper>
		</FieldDecorator>
	);
};

interface Props {
	label: string;
	name: string;
	onValueChange: (name: string, value: string, errorMessage: string) => void;
	fieldId?: string;
	value?: Date;
	defaultValue?: Date;
	min?: Date;
	max?: Date;
	errorMessage?: string;
	required?: boolean;
	disabled?: boolean;
	displayOptionalLabel?: boolean;
	width?: string;
}

DateField.defaultProps = {
	fieldId: null,
	value: null,
	defaultValue: null,
	min: new Date(1900, 1, 1),
	max: new Date(),
	errorMessage: null,
	required: true,
	disabled: false,
	displayOptionalLabel: false,
	width: undefined
};

export default DateField;

// TODO: The width code was copied from DropDownListWrapper in DropDownListField.tsx. Is there a simple way to share the
// code?
const DateInputWrapper = styled.div<{ width: string }>`
	${(props) => (hasValue(props.width) ? `width: ${props.width};` : '')}

	input {
		${baseInputCss}
		box-shadow: none;
	}
`;
